/* storybook-check-ignore */
import { Button, Icon, Flex, Image } from '@opendoor/bricks/core';
import Share from '@opendoor/bricks/core/Icon/SpritesheetIcons/Share';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import { Modal, setAppElement } from '@opendoor/bricks/complex';
import { useCallback, useEffect, useState } from 'react';
import AddressDisplay, { AddressDisplayProps } from './AddressDisplay';
import { PropertyOverviewBasic, PropertyOverviewProps } from './PropertyOverview';
import { numberAsDollar } from '../helpers/format';
import useTrackProperty from '../helpers/useTrackProperty';

type ShareButtonProps = {
  address: AddressDisplayProps;
  overview: PropertyOverviewProps;
  listPrice?: number | null;
  coverPhoto?: string;
};

const ShareButton: React.FC<ShareButtonProps & { setModalElement: boolean }> = (props) => {
  const { trackProperty } = useTrackProperty();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopied, setCopied] = useState(false);
  useEffect(() => {
    if (props.setModalElement) {
      setAppElement('#__next');
    }
  }, []);
  const onShareClick = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: window.location.toString(),
        });
      } catch (e: any) {
        setIsModalOpen(true);
      }
    } else {
      setIsModalOpen(true);
    }
  }, []);
  const onCopyClick = useCallback(async () => {
    trackProperty('property-share', 'copy');
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(window.location.toString());
        setCopied(true);
      } catch (e) {
        setCopied(false);
      }
    }
  }, []);
  const formattedListPrice =
    typeof props.listPrice === 'number' ? numberAsDollar(props.listPrice / 100) : null;
  return (
    <>
      <SelectiveSpritesheet icons={[Share]} />
      <Modal
        analyticsName="cosmos-property-share-modal"
        showClose={true}
        headerProps={{ title: 'Share' }}
        onRequestClose={() => {
          setIsModalOpen(false);
          setCopied(false);
        }}
        hasPadding={true}
        desktopAndTabletSize="sm"
        mobileSize="fullScreen"
        isOpen={isModalOpen}
      >
        <Flex
          marginY="auto"
          marginX="auto"
          alignItems="center"
          width="75%"
          flexDirection="column"
          gap="7"
          mb="6"
        >
          <Flex gap="4" justifyContent="center" marginY="auto" height="100%">
            {props.coverPhoto && (
              <Image
                src={`${props.coverPhoto}?${new URLSearchParams({
                  preset: 'square-128',
                  fit: 'crop',
                  service: 'cosmos',
                }).toString()}`}
                width="100px"
                height="100px"
                alt=""
                borderRadius="roundedSmooth"
              ></Image>
            )}
            <Flex flexDirection="column" justifyContent="space-evenly" marginY="auto">
              <AddressDisplay
                {...props.address}
                as="h2"
                fontSize="s1"
                mb="0"
                mt="0"
                fontWeight="semibold"
              />
              <PropertyOverviewBasic {...props.overview} extras={[{ value: formattedListPrice }]} />
            </Flex>
          </Flex>
          <Button
            disabled={isCopied}
            onClick={onCopyClick}
            aria-label="Copy Address"
            analyticsName="cosmos-pdp-copy-link-share"
          >
            {isCopied ? 'Copied!' : 'Copy link'}
          </Button>
        </Flex>
      </Modal>
      <Button
        variant="icon"
        aria-label="Favorite this property"
        analyticsName="cosmos-pdp-favorite-button"
        type="button"
        onClick={() => {
          trackProperty('property-share', 'click');
          onShareClick().finally();
        }}
      >
        <Icon name={'share'} size={16}></Icon>
      </Button>
    </>
  );
};

export default ShareButton;
